import { FormEvent, useCallback, useEffect, useState } from 'react';
import {
  adminCreateRealmUser,
  adminInviteRealmUser,
  adminUpdateRealmUser,
  fetchAssignableEcosystems,
  generateTemporaryPassword,
  type AssignableEcosystem,
  type CreateUserResult,
  type InviteUserResult,
  type RealmUserSearchResult,
} from '@frontend/api-clients/adminUserClient';
import { authClient } from '@frontend/api-clients/authClient';
import type { CreateUserPanelProps } from './types';
import AdminOrchSplitPane from './AdminOrchSplitPane';
import RealmUsersDirectory from './RealmUsersDirectory';

type ProvisionMode = 'create' | 'invite';
type PanelMode = 'idle' | 'view' | 'edit' | 'create';

interface ProvisionOutcome {
  kind: 'create' | 'invite';
  userId: string;
  username: string;
  email: string;
  ecosystemAttach?: CreateUserResult['ecosystemAttach'];
  inviteSent?: boolean;
  passwordTemporary?: boolean;
}

function syncStatusLabel(status: string): { label: string; className: string } {
  if (status === 'joined' || status === 'already_member') {
    return { label: status === 'joined' ? 'Joined' : 'Already member', className: 'status-up' };
  }
  return { label: status, className: 'status-down' };
}

export default function CreateUserPanel({
  expanded,
  onToggle,
  onProvisionSuccess,
  ecosystemAttachMode = 'optional',
  scopedEcosystemId = '',
  showUserDirectory = true,
}: CreateUserPanelProps) {
  const [mode, setMode] = useState<ProvisionMode>('create');
  const [panelMode, setPanelMode] = useState<PanelMode>('idle');
  const [selectedUser, setSelectedUser] = useState<RealmUserSearchResult | null>(null);
  const [assignable, setAssignable] = useState<AssignableEcosystem[]>([]);
  const [loadingEcosystems, setLoadingEcosystems] = useState(false);
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [selectedEcosystems, setSelectedEcosystems] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [outcome, setOutcome] = useState<ProvisionOutcome | null>(null);
  const [directoryRefreshKey, setDirectoryRefreshKey] = useState(0);

  const loadAssignable = useCallback(async () => {
    if (ecosystemAttachMode === 'none') {
      setAssignable([]);
      setSelectedEcosystems({});
      return;
    }
    setLoadingEcosystems(true);
    try {
      const token = authClient.getToken();
      if (!token) return;
      const rows = await fetchAssignableEcosystems(token);
      const scoped = scopedEcosystemId.trim();
      const visible =
        ecosystemAttachMode === 'scoped' && scoped
          ? rows.filter((row) => row.id === scoped)
          : rows;
      setAssignable(visible);
      setSelectedEcosystems((current) => {
        const next: Record<string, boolean> = {};
        for (const row of visible) {
          if (ecosystemAttachMode === 'scoped' && scoped && row.id === scoped) {
            next[row.id] = true;
          } else {
            next[row.id] = current[row.id] ?? false;
          }
        }
        return next;
      });
    } catch (loadErr) {
      setError(loadErr instanceof Error ? loadErr.message : 'Failed to load ecosystems');
    } finally {
      setLoadingEcosystems(false);
    }
  }, [ecosystemAttachMode, scopedEcosystemId]);

  useEffect(() => {
    if (!expanded) return;
    void loadAssignable();
  }, [expanded, loadAssignable]);

  const selectedEcosystemIds = assignable
    .filter((e) => selectedEcosystems[e.id])
    .map((e) => e.id);

  const toggleEcosystem = (id: string) => {
    setSelectedEcosystems((current) => ({ ...current, [id]: !current[id] }));
  };

  const handleGeneratePassword = () => {
    setPassword(generateTemporaryPassword());
  };

  const resetCreateForm = () => {
    setUsername('');
    setEmail('');
    setFirstName('');
    setLastName('');
    setPassword('');
    setEnabled(true);
    setOutcome(null);
    setError('');
    setSelectedEcosystems(() => {
      const next: Record<string, boolean> = {};
      const scoped = scopedEcosystemId.trim();
      for (const row of assignable) {
        next[row.id] =
          ecosystemAttachMode === 'scoped' && scoped && row.id === scoped ? true : false;
      }
      return next;
    });
  };

  const enterCreateMode = () => {
    setSelectedUser(null);
    setPanelMode('create');
    setMode('create');
    resetCreateForm();
  };

  const onSelectUser = (user: RealmUserSearchResult | null) => {
    if (!user) {
      setSelectedUser(null);
      setPanelMode('idle');
      return;
    }
    setSelectedUser(user);
    setPanelMode('view');
    setOutcome(null);
    setError('');
    setUsername(user.username || '');
    setEmail(user.email || '');
    setFirstName(user.firstName || '');
    setLastName(user.lastName || '');
    setEnabled(user.enabled !== false);
  };

  const beginEdit = () => {
    if (!selectedUser) return;
    setPanelMode('edit');
    setError('');
    setUsername(selectedUser.username || '');
    setEmail(selectedUser.email || '');
    setFirstName(selectedUser.firstName || '');
    setLastName(selectedUser.lastName || '');
    setEnabled(selectedUser.enabled !== false);
  };

  const cancelEdit = () => {
    if (!selectedUser) {
      setPanelMode('idle');
      return;
    }
    setPanelMode('view');
    setError('');
    setUsername(selectedUser.username || '');
    setEmail(selectedUser.email || '');
    setFirstName(selectedUser.firstName || '');
    setLastName(selectedUser.lastName || '');
    setEnabled(selectedUser.enabled !== false);
  };

  const onConfirmEdit = async () => {
    if (!selectedUser) return;
    setBusy(true);
    setError('');
    try {
      const token = authClient.getToken();
      if (!token) throw new Error('You must be signed in.');
      const updated = await adminUpdateRealmUser(token, selectedUser.userId, {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim().toLowerCase(),
        enabled,
      });
      setSelectedUser({ ...selectedUser, ...updated });
      setPanelMode('view');
      setDirectoryRefreshKey((k) => k + 1);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update user');
    } finally {
      setBusy(false);
    }
  };

  const onSubmitCreate = async (event: FormEvent) => {
    event.preventDefault();
    setError('');
    setOutcome(null);
    setBusy(true);

    try {
      const token = authClient.getToken();
      if (!token) throw new Error('You must be signed in.');

      if (mode === 'create') {
        if (!password || password.length < 8) {
          throw new Error('A temporary password of at least 8 characters is required.');
        }
        const result = await adminCreateRealmUser(token, {
          username: username.trim(),
          email: email.trim().toLowerCase(),
          password,
          firstName: firstName.trim() || undefined,
          lastName: lastName.trim() || undefined,
          ecosystemIds: selectedEcosystemIds,
        });
        setOutcome({
          kind: 'create',
          userId: result.userId,
          username: result.username,
          email: result.email,
          ecosystemAttach: result.ecosystemAttach,
          passwordTemporary: result.passwordTemporary,
        });
        setDirectoryRefreshKey((k) => k + 1);
        onProvisionSuccess?.();
      } else {
        const result: InviteUserResult = await adminInviteRealmUser(token, {
          username: username.trim(),
          email: email.trim().toLowerCase(),
          firstName: firstName.trim() || undefined,
          lastName: lastName.trim() || undefined,
        });
        setOutcome({
          kind: 'invite',
          userId: result.userId,
          username: result.username,
          email: result.email,
          inviteSent: result.inviteSent,
        });
        setDirectoryRefreshKey((k) => k + 1);
        onProvisionSuccess?.();
      }
    } catch (submitErr) {
      const e = submitErr as Error & { code?: string; existingUserId?: string; hint?: string };
      let msg = e.message || 'Request failed';
      if (e.code === 'KC_EMAIL_EXISTS' && e.existingUserId) {
        msg = `${msg} (user ${e.existingUserId})`;
        if (e.hint) msg += ` — ${e.hint}`;
      }
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  const ecosystemChecklist =
    mode === 'create' && ecosystemAttachMode !== 'none' ? (
      <fieldset className="admin-ecosystem-checkboxes">
        <legend>
          {ecosystemAttachMode === 'scoped'
            ? 'Ecosystem membership'
            : 'Ecosystem membership (optional)'}
        </legend>
        {ecosystemAttachMode === 'optional' ? (
          <p className="ecosystem-description">
            Leave unchecked for realm-only. Checking a box joins that ecosystem’s member group
            immediately — only select ecosystems you intend to grant.
          </p>
        ) : null}
        {loadingEcosystems ? (
          <p className="ecosystem-description">Loading ecosystems…</p>
        ) : assignable.length === 0 ? (
          <p className="ecosystem-description">No ecosystems available for your admin scope.</p>
        ) : (
          assignable.map((eco) => (
            <label key={eco.id} className="admin-ecosystem-check">
              <input
                type="checkbox"
                checked={Boolean(selectedEcosystems[eco.id])}
                onChange={() => toggleEcosystem(eco.id)}
              />
              <span>
                {eco.displayName} <code className="admin-inline-code">{eco.memberGroup}</code>
              </span>
            </label>
          ))
        )}
      </fieldset>
    ) : mode === 'create' && ecosystemAttachMode === 'none' ? (
      <p className="ecosystem-description">
        Creates a realm identity only. Grant ecosystem access afterward via Ecosystem membership
        attach, or by adding the user to a project (auto-attaches that project’s ecosystem).
      </p>
    ) : null;

  const createForm = (
    <form className="admin-member-form admin-create-user-form" onSubmit={onSubmitCreate}>
      <div className="admin-user-mode-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          className={`btn btn-secondary${mode === 'create' ? ' btn-primary' : ''}`}
          aria-selected={mode === 'create'}
          onClick={() => setMode('create')}
        >
          Create + temp password
        </button>
        <button
          type="button"
          role="tab"
          className={`btn btn-secondary${mode === 'invite' ? ' btn-primary' : ''}`}
          aria-selected={mode === 'invite'}
          onClick={() => setMode('invite')}
        >
          Email invite
        </button>
      </div>

      <input
        type="text"
        placeholder="Username (not email)"
        value={username}
        onChange={(e) => setUsername(e.target.value)}
        required
        autoComplete="off"
        pattern="[a-zA-Z0-9._-]{3,64}"
        title="3–64 characters: letters, digits, . _ -"
      />
      <input
        type="email"
        placeholder="Email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
        autoComplete="off"
      />
      <input
        type="text"
        placeholder="First name (optional)"
        value={firstName}
        onChange={(e) => setFirstName(e.target.value)}
        autoComplete="off"
      />
      <input
        type="text"
        placeholder="Last name (optional)"
        value={lastName}
        onChange={(e) => setLastName(e.target.value)}
        autoComplete="off"
      />

      {mode === 'create' ? (
        <div className="admin-create-user-password-row">
          <input
            type="text"
            placeholder="Temporary password (min 8 chars)"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={8}
            autoComplete="new-password"
          />
          <button type="button" className="btn btn-secondary" onClick={handleGeneratePassword}>
            Generate
          </button>
        </div>
      ) : null}

      {ecosystemChecklist}

      {mode === 'invite' ? (
        <p className="ecosystem-description">
          After invite, attach ecosystems via Ecosystem membership or add the user to a project.
        </p>
      ) : null}

      {error ? <div className="settings-message settings-error">{error}</div> : null}

      <div className="admin-create-user-actions">
        <button type="submit" className="btn btn-primary" disabled={busy}>
          {busy ? 'Working…' : mode === 'create' ? 'Confirm' : 'Send invite'}
        </button>
        <button
          type="button"
          className="btn btn-secondary"
          disabled={busy}
          onClick={() => {
            resetCreateForm();
            if (showUserDirectory) setPanelMode('idle');
          }}
        >
          Cancel
        </button>
      </div>
    </form>
  );

  const displayName = (() => {
    if (!selectedUser) return '';
    const fromDraft =
      panelMode === 'edit'
        ? [firstName, lastName].map((s) => s.trim()).filter(Boolean).join(' ')
        : [selectedUser.firstName, selectedUser.lastName].filter(Boolean).join(' ').trim();
    return fromDraft || selectedUser.username || selectedUser.email || selectedUser.userId;
  })();

  const enabledNow = panelMode === 'edit' ? enabled : selectedUser?.enabled !== false;
  const verifiedNow = selectedUser?.emailVerified === true;

  const userDetails =
    selectedUser && (panelMode === 'view' || panelMode === 'edit') ? (
      <div className="admin-orch-detail">
        <div className="admin-orch-detail-header">
          <h4>User details</h4>
          <div className="admin-orch-detail-actions">
            {panelMode === 'edit' ? (
              <>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={busy}
                  onClick={() => void onConfirmEdit()}
                >
                  {busy ? 'Saving…' : 'Confirm'}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  disabled={busy}
                  onClick={cancelEdit}
                >
                  Cancel
                </button>
              </>
            ) : (
              <button type="button" className="btn btn-secondary" onClick={beginEdit}>
                Edit
              </button>
            )}
          </div>
        </div>

        {error ? (
          <div className="settings-message settings-error" role="alert">
            {error}
          </div>
        ) : null}

        <div className="admin-orch-detail-name-row">
          <p className="admin-orch-detail-display-name">{displayName}</p>
          {panelMode === 'edit' ? (
            <button
              type="button"
              className={`admin-status-badge ${enabledNow ? 'status-up' : 'status-down'}`}
              disabled={busy}
              aria-pressed={enabledNow}
              title="Toggle account enabled"
              onClick={() => setEnabled((v) => !v)}
            >
              {enabledNow ? 'Enabled' : 'Disabled'}
            </button>
          ) : (
            <span className={`admin-status-badge ${enabledNow ? 'status-up' : 'status-down'}`}>
              {enabledNow ? 'Enabled' : 'Disabled'}
            </span>
          )}
        </div>

        <div className="admin-orch-detail-fields">
          <div className="admin-orch-field">
            <span>Username</span>
            <p className="admin-orch-detail-readonly">
              <code className="admin-inline-code">{selectedUser.username}</code>
            </p>
          </div>
          <label className="admin-orch-field">
            <span>Email</span>
            <div className="admin-orch-detail-inline">
              {panelMode === 'edit' ? (
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={busy}
                />
              ) : (
                <p className="admin-orch-detail-readonly">{selectedUser.email || '—'}</p>
              )}
              <span className={`admin-status-badge ${verifiedNow ? 'status-up' : 'status-unknown'}`}>
                {verifiedNow ? 'Verified' : 'Unverified'}
              </span>
            </div>
          </label>
          <label className="admin-orch-field">
            <span>First name</span>
            {panelMode === 'edit' ? (
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                disabled={busy}
              />
            ) : (
              <p className="admin-orch-detail-readonly">{selectedUser.firstName || '—'}</p>
            )}
          </label>
          <label className="admin-orch-field">
            <span>Last name</span>
            {panelMode === 'edit' ? (
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                disabled={busy}
              />
            ) : (
              <p className="admin-orch-detail-readonly">{selectedUser.lastName || '—'}</p>
            )}
          </label>
        </div>
      </div>
    ) : null;

  const outcomeBlock = outcome ? (
    <div className="admin-provision-outcome">
      <h4>Provision status</h4>
      <ul className="admin-sync-status-list">
        <li>
          <span className="admin-sync-status-label">Keycloak identity</span>
          <span className="admin-sync-status-value status-up">Created</span>
        </li>
        <li>
          <span className="admin-sync-status-label">User id</span>
          <span className="admin-sync-status-value">
            <code className="admin-inline-code">{outcome.userId}</code>
          </span>
        </li>
        {outcome.kind === 'create' && outcome.passwordTemporary ? (
          <li>
            <span className="admin-sync-status-label">Password</span>
            <span className="admin-sync-status-value status-unknown">
              Temporary — user must change on first login
            </span>
          </li>
        ) : null}
        {outcome.kind === 'invite' ? (
          <li>
            <span className="admin-sync-status-label">Invite email</span>
            <span
              className={`admin-sync-status-value ${outcome.inviteSent ? 'status-up' : 'status-down'}`}
            >
              {outcome.inviteSent ? 'Sent via Keycloak' : 'User created; email may have failed'}
            </span>
          </li>
        ) : null}
        {(outcome.ecosystemAttach || []).map((row) => {
          const st = syncStatusLabel(row.status);
          return (
            <li key={row.ecosystemId}>
              <span className="admin-sync-status-label">Ecosystem {row.ecosystemId}</span>
              <span className={`admin-sync-status-value ${st.className}`}>{st.label}</span>
            </li>
          );
        })}
      </ul>
    </div>
  ) : null;

  return (
    <div className="ecosystem-card admin-widget-card">
      <button type="button" className="admin-widget-header" onClick={onToggle}>
        <div className="admin-widget-header-main">
          <h3 className="ecosystem-title">Realm users</h3>
          <span className="admin-status-badge status-unknown">Keycloak</span>
        </div>
        <span className="admin-widget-toggle">{expanded ? '▾' : '▸'}</span>
      </button>

      {expanded ? (
        <div className="admin-widget-body">
          <p className="ecosystem-description">
            Browse realm users or create a Keycloak identity with a temporary password / email
            invite. Downstream apps provision on first SSO login.
          </p>

          {showUserDirectory ? (
            <AdminOrchSplitPane
              className="admin-orch-master-detail"
              defaultLeftWidth="50%"
              resizeLabel="Resize realm users list and details"
              left={
                <RealmUsersDirectory
                  refreshKey={directoryRefreshKey}
                  selectedUserId={panelMode === 'create' ? null : selectedUser?.userId ?? null}
                  onSelect={onSelectUser}
                />
              }
              right={
                <div>
                  <div className="admin-orch-section-header">
                    <h4 className="admin-orch-subtitle">
                      {panelMode === 'create' ? 'Create user' : 'Details'}
                    </h4>
                    <button type="button" className="btn btn-primary" onClick={enterCreateMode}>
                      Create user
                    </button>
                  </div>
                  {panelMode === 'create' ? (
                    <div className="admin-orch-detail">
                      {createForm}
                      {outcomeBlock}
                    </div>
                  ) : userDetails ? (
                    userDetails
                  ) : (
                    <div className="admin-orch-detail">
                      <p className="admin-orch-detail-empty">
                        Select a user to view details, or create a new user.
                      </p>
                      {outcomeBlock}
                    </div>
                  )}
                </div>
              }
            />
          ) : (
            <div className="admin-create-user-form-column">
              {createForm}
              {outcomeBlock}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}
